import type { ApiSchemas } from '@provisium/web-api'

import { apiRequest } from './client'

export type SupplierCustomerDto = {
  restaurantOrganizationId: number
  restaurantName: string
  contactFullName: string | null
  phone: string | null
  email: string | null
  ordersCount: number
  lastRfqRequestId: number | null
  lastOrderedAt: string | null
}

export type SupplierCustomersPageDto = {
  items: SupplierCustomerDto[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

// Тип провода берём из контракта: он генерируется из openapi.json, который печётся
// из кода бэкенда. Уберут или переименуют поле — здесь будет ошибка компиляции,
// а не тихий undefined в рантайме. Раньше здесь был Record<string, unknown>, и именно
// поэтому переименование restaurant*→buyer* (2aa43db) прошло незамеченным.
// camelCase-часть — совместимость со старым бэкендом (до раскатки snake в прод),
// снимается на шаге 4.
type CustomerRaw = ApiSchemas['MyCustomerDto'] & {
  buyerOrganizationId?: number
  buyerName?: string
  contactFullName?: string | null
  ordersCount?: number
  lastRfqRequestId?: number | null
  lastOrderedAt?: string | null
}
type CustomersPageRaw = ApiSchemas['MyCustomersPageDto'] & {
  items?: CustomerRaw[]
  totalElements?: number
  totalPages?: number
}

const normalizeCustomer = (raw: CustomerRaw): SupplierCustomerDto => ({
  restaurantOrganizationId: raw.buyerOrganizationId ?? raw.buyer_organization_id ?? 0,
  restaurantName: raw.buyerName ?? raw.buyer_name ?? '',
  contactFullName: raw.contactFullName ?? raw.contact_full_name ?? null,
  phone: raw.phone ?? null,
  email: raw.email ?? null,
  ordersCount: Number(raw.ordersCount ?? raw.orders_count ?? 0),
  lastRfqRequestId: Number(raw.lastRfqRequestId ?? raw.last_rfq_request_id) || null,
  lastOrderedAt: raw.lastOrderedAt ?? raw.last_ordered_at ?? null,
})

const normalizePage = (raw: CustomersPageRaw): SupplierCustomersPageDto => ({
  items: (raw.items ?? []).map(normalizeCustomer),
  page: Number(raw.page ?? 0),
  size: Number(raw.size ?? 0),
  totalElements: Number(raw.totalElements ?? raw.total_elements ?? 0),
  totalPages: Number(raw.totalPages ?? raw.total_pages ?? 0),
})

export const listSupplierCustomers = async (
  organizationId: number,
  page = 0,
  size = 50,
): Promise<SupplierCustomersPageDto> => {
  const result = await apiRequest<CustomersPageRaw>(
    `/supplier/customers/my?organizationId=${organizationId}&page=${page}&size=${size}`,
  )
  return normalizePage(result)
}
